import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Film, Crown, Calendar, User, Clapperboard, Building2, Trophy, PlayCircle, AlertCircle } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const ExploreMovies = () => {
  const subscriptionJoinLink = 'https://members.popcrownofficial.com/subscription/join';

  // Schema.org Movie object for the released film
  const movieSchema = {
    "@context": "https://schema.org",
    "@type": "Movie",
    "name": "Michael",
    "alternateName": "Michael (2026)",
    "description": "Michael is the 2026 biographical film about Michael Jackson, directed by Antoine Fuqua and starring Jaafar Jackson as the King of Pop. It opened to $217.4M globally — a new all-time record for a music biopic — and crossed $889M at the worldwide box office, becoming the highest-grossing Lionsgate film of all time.",
    "image": "https://img.youtube.com/vi/mbtgEE6rkxw/maxresdefault.jpg",
    "datePublished": "2026-04-24",
    "director": { "@type": "Person", "name": "Antoine Fuqua" },
    "actor": [{ "@type": "Person", "name": "Jaafar Jackson", "character": "Michael Jackson" }],
    "productionCompany": { "@type": "Organization", "name": "Lionsgate", "url": "https://www.lionsgate.com" },
    "about": { "@type": "Person", "name": "Michael Jackson", "alternateName": "King of Pop" },
    "trailer": [
      { "@type": "VideoObject", "name": "Michael - Official Trailer", "thumbnailUrl": "https://img.youtube.com/vi/mbtgEE6rkxw/maxresdefault.jpg", "uploadDate": "2025-01-29T00:00:00+00:00", "contentUrl": "https://www.youtube.com/watch?v=mbtgEE6rkxw", "embedUrl": "https://www.youtube.com/embed/mbtgEE6rkxw" },
      { "@type": "VideoObject", "name": "Michael - Trailer 2", "thumbnailUrl": "https://img.youtube.com/vi/723RZxnDWKE/maxresdefault.jpg", "uploadDate": "2025-02-10T00:00:00+00:00", "contentUrl": "https://www.youtube.com/watch?v=723RZxnDWKE", "embedUrl": "https://www.youtube.com/embed/723RZxnDWKE" }
    ]
  };

  const pageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Michael Jackson Biopic 2026 — Box Office Records, Amazon Prime Release & Sequel News",
    "description": "Everything fans need to know about Michael — the record-breaking 2026 Michael Jackson biopic. Box office, Amazon Prime release date, sequel status, trailers and cast.",
    "url": "https://popcrownofficial.com/explore/movies",
    "isPartOf": { "@type": "WebSite", "name": "PopCrown Official", "url": "https://popcrownofficial.com" }
  };

  return (
    <>
      <Helmet>
        <title>Michael (2026) — Box Office, Amazon Prime Release & Sequel News | PopCrown</title>
        <meta name="description" content="The Michael Jackson biopic 'Michael' (2026) crossed $889M worldwide and broke the all-time music-biopic opening record. Now on Amazon Prime Video, Apple TV, YouTube. Sequel in development at Lionsgate." />
        <meta name="keywords" content="Michael movie 2026, Michael Jackson biopic box office, Michael Amazon Prime, Michael movie streaming, Michael sequel, Michael 2, Jaafar Jackson, Antoine Fuqua, Lionsgate, Michael biopic records, Michael Jackson film, MJ movie review, Michael movie digital release, where to watch Michael movie" />
        <meta property="og:title" content="Michael (2026) — Box Office, Amazon Prime Release & Sequel News" />
        <meta property="og:description" content="The Michael Jackson biopic crossed $889M worldwide. Now available to rent or buy on Amazon Prime Video. Sequel in development." />
        <meta property="og:image" content="https://img.youtube.com/vi/mbtgEE6rkxw/maxresdefault.jpg" />
        <meta property="og:url" content="https://popcrownofficial.com/explore/movies" />
        <link rel="canonical" href="https://popcrownofficial.com/explore/movies" />
        <script type="application/ld+json">{JSON.stringify(movieSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(pageSchema)}</script>
      </Helmet>
    <div className="min-h-screen pt-20">
      {/* Header */}
      <section className="py-12" style={{ background: 'linear-gradient(180deg, #1a1a1a 0%, #000 100%)' }}>
        <div className="container mx-auto px-4">
          <Link to="/explore" className="inline-flex items-center gap-2 text-[#E10600] hover:text-white transition-colors mb-6">
            <ArrowLeft className="w-5 h-5" />
            Back to Explore
          </Link>
          <div className="flex items-center gap-4">
            <Film className="w-12 h-12 text-[#9333EA]" />
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-white">
                2026 <span className="thriller-text">Movie</span>
              </h1>
              <p className="text-gray-400">The record-breaking Michael Jackson biopic — now streaming</p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12" style={{ background: 'linear-gradient(180deg, #FFFFFF 0%, #FFF5F5 100%)' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Hero Card with Main Trailer */}
            <div className="rounded-2xl overflow-hidden mb-8" style={{
              background: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)',
              border: '2px solid rgba(147, 51, 234, 0.4)'
            }}>
              {/* YouTube Trailer Embed - Official Trailer */}
              <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                <iframe 
                  className="absolute top-0 left-0 w-full h-full"
                  src="https://www.youtube.com/embed/mbtgEE6rkxw?si=ww3ZuLWOfgs32e2A" 
                  title="Michael - Official Trailer"
                  frameBorder="0" 
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
                  referrerPolicy="strict-origin-when-cross-origin" 
                  allowFullScreen
                />
              </div>
              
              <div className="p-8 md:p-12 text-center">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#E10600] text-black rounded-full text-sm font-bold mb-6" data-testid="movies-status-badge">
                  <PlayCircle className="w-4 h-4" />
                  NOW ON AMAZON PRIME, APPLE TV & MORE
                </div>
                <h2 className="text-5xl md:text-7xl font-bold text-white mb-4" style={{ fontStyle: 'italic' }}>
                  Michael
                </h2>
                <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
                  The record-breaking biographical film about the life, music, and legacy of the King of Pop — released worldwide 24 April 2026 and now available at home.
                </p>
                <div className="mx-auto h-1 w-32 bg-gradient-to-r from-[#DC143C] via-[#E10600] to-[#DC143C]"></div>
              </div>
            </div>

            {/* Box Office Records Strip */}
            <div className="rounded-2xl p-6 md:p-8 mb-8" style={{
              background: 'linear-gradient(135deg, #0f0f0f 0%, #1a1a1a 100%)',
              border: '2px solid #E10600',
              boxShadow: '0 0 40px rgba(225, 6, 0, 0.2)'
            }} data-testid="movies-box-office-strip">
              <div className="flex items-center justify-center gap-3 mb-6">
                <Trophy className="w-7 h-7 text-[#E10600]" />
                <h3 className="text-2xl md:text-3xl font-bold text-white">Box Office Records Broken</h3>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                <div className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-[#E10600]">$217.4M</div>
                  <div className="text-xs md:text-sm text-gray-400 mt-1">Global opening &mdash; all-time music-biopic record</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-[#E10600]">$97.2M</div>
                  <div className="text-xs md:text-sm text-gray-400 mt-1">Domestic opening &mdash; biggest biographical/musical debut ever</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-[#E10600]">$1B+</div>
                  <div className="text-xs md:text-sm text-gray-400 mt-1">Worldwide gross &mdash; officially crossed the $1 billion milestone</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-[#E10600]">#1</div>
                  <div className="text-xs md:text-sm text-gray-400 mt-1">Highest-grossing music biopic of all time</div>
                </div>
              </div>
              <div className="mt-6 p-4 rounded-xl bg-black/40 border border-[#E10600]/30">
                <p className="text-sm text-gray-300 leading-relaxed">
                  <strong className="text-[#E10600]">Michael has officially crossed $1 billion worldwide</strong> &mdash; making it the <strong className="text-white">highest-grossing music biopic of all time</strong>, the <strong className="text-white">highest-grossing Lionsgate film ever</strong>, and one of the <strong className="text-white">top biographical films in cinema history</strong>. Japan&apos;s wide release on <strong className="text-white">12 June 2026</strong> helped push the film across the threshold.
                </p>
              </div>
              <p className="text-center text-xs text-gray-500 mt-4">
                Latest verified figures: Box Office Mojo, The Numbers (as of June 2026). PopCrown updates these as new data lands.
              </p>
            </div>

            {/* Chart Domination — Soundtrack #1s */}
            <div className="rounded-2xl p-6 md:p-8 mb-8" style={{
              background: 'linear-gradient(135deg, #1a1a1a 0%, #2a1a0a 100%)',
              border: '2px solid #DC143C',
              boxShadow: '0 0 40px rgba(220, 20, 60, 0.2)'
            }} data-testid="movies-chart-domination">
              <div className="flex items-center justify-center gap-3 mb-2">
                <Trophy className="w-7 h-7 text-[#DC143C]" />
                <h3 className="text-2xl md:text-3xl font-bold text-white">#1 Across The Globe</h3>
              </div>
              <p className="text-center text-gray-400 mb-6 text-sm md:text-base">
                The biopic effect is everywhere &mdash; the official soundtrack, Michael&apos;s own autobiography <em>Moonwalk</em>, and his classic albums all hit #1 around the world.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 rounded-xl bg-black/40 border border-[#E10600]/40 flex items-center gap-4">
                  <div className="text-4xl">🇬🇧</div>
                  <div>
                    <div className="text-[#E10600] font-bold text-lg">#1 UK Soundtrack Albums</div>
                    <div className="text-gray-400 text-sm">Official Charts Company &mdash; soundtrack album peak</div>
                  </div>
                </div>
                <div className="p-4 rounded-xl bg-black/40 border border-[#E10600]/40 flex items-center gap-4">
                  <div className="text-4xl">🇳🇿</div>
                  <div>
                    <div className="text-[#E10600] font-bold text-lg">#1 New Zealand</div>
                    <div className="text-gray-400 text-sm">Recorded Music NZ album chart peak</div>
                  </div>
                </div>
                <div className="p-4 rounded-xl bg-black/40 border border-[#E10600]/40 flex items-center gap-4">
                  <div className="text-4xl">📖</div>
                  <div>
                    <div className="text-[#E10600] font-bold text-lg">#1 Amazon &mdash; <em>Moonwalk</em></div>
                    <div className="text-gray-400 text-sm">Michael&apos;s own autobiography surged back to the top of Amazon&apos;s best-seller charts following the biopic. Originally a #1 New York Times best-seller with over 1 million copies sold.</div>
                  </div>
                </div>
                <div className="p-4 rounded-xl bg-black/40 border border-[#E10600]/40 flex items-center gap-4">
                  <div className="text-4xl">🇺🇸</div>
                  <div>
                    <div className="text-[#E10600] font-bold text-lg">#2 US Billboard Soundtracks</div>
                    <div className="text-gray-400 text-sm">#37 on the all-genre US Billboard 200</div>
                  </div>
                </div>
                <div className="p-4 rounded-xl bg-black/40 border border-[#E10600]/40 flex items-center gap-4 md:col-span-2">
                  <div className="text-4xl">🌍</div>
                  <div>
                    <div className="text-[#E10600] font-bold text-lg">Catalogue Resurrection</div>
                    <div className="text-gray-400 text-sm"><em>Thriller</em>, <em>Number Ones</em> &amp; classic MJ albums climbing worldwide iTunes / streaming charts &mdash; the biopic effect rippling across decades of his work.</div>
                  </div>
                </div>
              </div>
              <p className="text-center text-xs text-gray-500 mt-6">
                Sources: Wikipedia (Michael soundtrack), MJWorld chart reporting, Sony Music Legacy Recordings.
              </p>
            </div>

            {/* Watch At Home — Amazon Prime CTA */}
            <div className="rounded-2xl p-6 md:p-8 mb-8" style={{
              background: 'linear-gradient(135deg, #1a1a1a 0%, #2a1a3a 100%)',
              border: '2px solid #9333EA'
            }} data-testid="movies-watch-at-home">
              <div className="flex items-center gap-3 mb-4">
                <PlayCircle className="w-7 h-7 text-[#9333EA]" />
                <h3 className="text-2xl md:text-3xl font-bold text-white">Watch It At Home</h3>
              </div>
              <p className="text-gray-300 mb-2">
                <strong className="text-white">Digital rental &amp; purchase</strong> available since <strong className="text-[#E10600]">9 June 2026</strong> on:
              </p>
              <div className="flex flex-wrap gap-2 mb-5">
                {['Amazon Prime Video', 'Apple TV', 'Fandango at Home', 'Google TV', 'YouTube'].map((p) => (
                  <span key={p} className="px-3 py-1.5 bg-black/40 border border-[#9333EA]/50 text-white text-sm rounded-full">
                    {p}
                  </span>
                ))}
              </div>
              <p className="text-gray-400 text-sm mb-5">
                Note: this is a premium digital release (rent or buy) — it is <em>not</em> currently included with an Amazon Prime subscription. Physical release on <strong className="text-white">DVD, Blu-ray and 4K UHD: 14 July 2026</strong>.
              </p>
              <a
                href="https://www.amazon.com/s?k=Michael+2026+Jaafar+Jackson&i=instant-video"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold text-white transition-all hover:scale-[1.03]"
                style={{ background: 'linear-gradient(135deg, #9333EA 0%, #DC143C 100%)' }}
                data-testid="movies-amazon-prime-cta"
              >
                <PlayCircle className="w-5 h-5" /> Find Michael on Amazon Prime Video
              </a>
            </div>

            {/* Second Trailer */}
            <div className="rounded-2xl overflow-hidden mb-12" style={{
              background: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)',
              border: '2px solid rgba(225, 6, 0, 0.3)'
            }}>
              <div className="p-4 text-center border-b border-gray-700">
                <span className="text-[#E10600] font-bold">Trailer 2</span>
              </div>
              <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                <iframe 
                  className="absolute top-0 left-0 w-full h-full"
                  src="https://www.youtube.com/embed/723RZxnDWKE?si=fQyxeSqE_1moXDGX" 
                  title="Michael - Trailer 2"
                  frameBorder="0" 
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
                  referrerPolicy="strict-origin-when-cross-origin" 
                  allowFullScreen
                />
              </div>
            </div>

            {/* Details Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
              <div className="p-6 rounded-xl" style={{
                background: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)',
                border: '2px solid rgba(225, 6, 0, 0.3)'
              }}>
                <div className="flex items-center gap-3 mb-4">
                  <User className="w-6 h-6 text-[#E10600]" />
                  <h3 className="text-lg font-bold text-white">Starring</h3>
                </div>
                <p className="text-2xl font-bold text-[#E10600] mb-2">Jaafar Jackson</p>
                <p className="text-gray-400 text-sm">Michael Jackson&apos;s nephew brought the King of Pop to life on the big screen — performance widely praised by critics and fans.</p>
              </div>

              <div className="p-6 rounded-xl" style={{
                background: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)',
                border: '2px solid rgba(225, 6, 0, 0.3)'
              }}>
                <div className="flex items-center gap-3 mb-4">
                  <Clapperboard className="w-6 h-6 text-[#E10600]" />
                  <h3 className="text-lg font-bold text-white">Director</h3>
                </div>
                <p className="text-2xl font-bold text-[#E10600] mb-2">Antoine Fuqua</p>
                <p className="text-gray-400 text-sm">Academy Award-winning director of &ldquo;Training Day&rdquo; and &ldquo;The Equalizer&rdquo;.</p>
              </div>

              <div className="p-6 rounded-xl" style={{
                background: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)',
                border: '2px solid rgba(225, 6, 0, 0.3)'
              }}>
                <div className="flex items-center gap-3 mb-4">
                  <Building2 className="w-6 h-6 text-[#E10600]" />
                  <h3 className="text-lg font-bold text-white">Studio</h3>
                </div>
                <p className="text-2xl font-bold text-[#E10600] mb-2">Lionsgate</p>
                <p className="text-gray-400 text-sm">Produced in collaboration with the Michael Jackson Estate. Reported budget: $155&ndash;200M.</p>
              </div>

              <div className="p-6 rounded-xl" style={{
                background: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)',
                border: '2px solid rgba(225, 6, 0, 0.3)'
              }}>
                <div className="flex items-center gap-3 mb-4">
                  <Calendar className="w-6 h-6 text-[#E10600]" />
                  <h3 className="text-lg font-bold text-white">Release Timeline</h3>
                </div>
                <p className="text-base text-[#E10600] mb-1"><strong>24 Apr 2026</strong> — Cinemas worldwide</p>
                <p className="text-base text-[#E10600] mb-1"><strong>9 Jun 2026</strong> — Digital rent / buy</p>
                <p className="text-base text-[#E10600]"><strong>14 Jul 2026</strong> — DVD, Blu-ray, 4K UHD</p>
              </div>
            </div>

            {/* Sequel Watch — Michael 2 */}
            <div className="p-6 md:p-8 rounded-xl mb-12" style={{
              background: 'linear-gradient(135deg, rgba(220, 20, 60, 0.08) 0%, rgba(225, 6, 0, 0.08) 100%)',
              border: '2px solid rgba(220, 20, 60, 0.4)'
            }} data-testid="movies-sequel-section">
              <div className="flex items-center gap-3 mb-4">
                <AlertCircle className="w-7 h-7 text-[#DC143C]" />
                <h3 className="text-2xl md:text-3xl font-bold text-black">Sequel Watch — &ldquo;Michael 2&rdquo;</h3>
              </div>
              <p className="text-gray-700 mb-4">
                A second film is <strong>in active development at Lionsgate</strong> &mdash; but it has <strong>not yet been formally greenlit</strong> at the time of writing. Here&apos;s the verified state of play:
              </p>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-3">
                  <span className="text-[#DC143C] font-bold mt-1">&bull;</span>
                  <span><strong>Lionsgate studio chair Adam Fogelson</strong> has publicly stated there is &ldquo;more story to tell&rdquo; and that a sequel is being actively discussed.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#DC143C] font-bold mt-1">&bull;</span>
                  <span><strong>Reports point to a planned 2026 shoot</strong> &mdash; which would likely make <strong>Antoine Fuqua unavailable</strong> due to another commitment. A new director would need to be attached.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#DC143C] font-bold mt-1">&bull;</span>
                  <span><strong>Unused footage from the first film</strong> is reported to be in play as part of how the second film is being constructed.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#DC143C] font-bold mt-1">&bull;</span>
                  <span>Covering the <strong>later, more contested portions</strong> of Michael Jackson&apos;s life remains the creative bottleneck &mdash; tied up in legal and estate considerations.</span>
                </li>
              </ul>
              <p className="text-sm text-gray-500 mt-5">
                Sources: Vanity Fair, Film Stories, Lionsgate executive statements (latest reporting). PopCrown will update this section the moment Lionsgate officially confirms.
              </p>
            </div>

            {/* What It Delivered */}
            <div className="p-8 rounded-xl mb-12" style={{
              background: 'linear-gradient(135deg, rgba(225, 6, 0, 0.1) 0%, rgba(220, 20, 60, 0.1) 100%)',
              border: '2px solid rgba(225, 6, 0, 0.3)'
            }}>
              <h3 className="text-2xl font-bold text-black mb-6 text-center">What It Delivered</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="text-3xl mb-3">🎵</div>
                  <h4 className="font-bold text-gray-800 mb-2">The Music</h4>
                  <p className="text-gray-600 text-sm">Behind the scenes of the greatest hits and legendary performances.</p>
                </div>
                <div className="text-center">
                  <div className="text-3xl mb-3">👨&zwj;👩&zwj;👧&zwj;👦</div>
                  <h4 className="font-bold text-gray-800 mb-2">The Family</h4>
                  <p className="text-gray-600 text-sm">From Gary, Indiana to global superstardom with the Jackson 5.</p>
                </div>
                <div className="text-center">
                  <div className="text-3xl mb-3">⭐</div>
                  <h4 className="font-bold text-gray-800 mb-2">The Legacy</h4>
                  <p className="text-gray-600 text-sm">The impact that changed music, dance, and entertainment forever.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-16" style={{ background: 'linear-gradient(135deg, #1a1a1a 0%, #000 100%)' }}>
        <div className="container mx-auto px-4 text-center">
          <img src="/popcrown-crown-red.png" alt="" aria-hidden="true" className="w-10 h-10 mx-auto mb-4 object-contain" />
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            2026 Was The Year of the <span className="thriller-text">FANS</span>
          </h2>
          <p className="text-gray-400 mb-6 max-w-xl mx-auto">
            Join the community where the conversation continues &mdash; the film, the records, the sequel watch, and everything next.
          </p>
          <a href={subscriptionJoinLink} target="_blank" rel="noopener noreferrer" className="btn-gold inline-block">
            Join the Community
          </a>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-6 bg-black">
        <div className="container mx-auto px-4 text-center">
          <p className="text-gray-500 text-xs">
            PopCrown is an independent fan community. Not affiliated with Lionsgate, the Michael Jackson Estate, or any official entities. Box office and sequel information sourced from public reporting and may be updated as situations evolve.
          </p>
        </div>
      </section>
    </div>
    </>
  );
};

export default ExploreMovies;
